/**
 * VsMicrosoftProject — head-to-head comparison landing page. Not just a
 * table — hero, contextual "why-this-matters", scored capability grid,
 * three tenant-quotes, and dual CTA (PDF download + signup).
 *
 * Route: /vs/microsoft-project
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  Check, X, ArrowRight, ShieldCheck, Zap, FileText, GitBranch, Layers,
  Building2, Sparkles, TrendingUp, Anchor, Radar, Cpu, Users,
} from "lucide-react";
import { LegalFooter } from "./legal/Terms";
import useSEO from "../lib/useSEO";

const CROSS = <X size={14} className="inline text-red-600 shrink-0" strokeWidth={2.4}/>;
const TICK  = <Check size={14} className="inline text-emerald-700 shrink-0" strokeWidth={2.6}/>;

const ROWS = [
  {
    icon: Layers,
    cat: "Core design",
    mp:  { flag: CROSS, text: "General-purpose task, schedule & resource planning for any project type." },
    cap: { flag: TICK,  text: "Purpose-built delivery OS for SAP S/4HANA, Microsoft Dynamics 365 and Oracle Cloud ERP transformations. Every module knows what phase you're in." },
  },
  {
    icon: GitBranch,
    cat: "SAP Activate alignment",
    mp:  { flag: CROSS, text: "None — generic WBS / Gantt structure. Teams reinvent the framework in Excel." },
    cap: { flag: TICK,  text: "Native 134-step SAP Activate methodology baked into governance & tracking with 12-gate criteria." },
  },
  {
    icon: Cpu,
    cat: "Live ERP tenant connectors",
    mp:  { flag: CROSS, text: "No S/4HANA, D365 or Oracle Cloud data connection. Screenshots and CSV exports become the source of truth." },
    cap: { flag: TICK,  text: "Live OData/OAuth connectors to S/4HANA, D365 (Finance/Sales/SCM/CX) and Oracle Cloud ERP (Financials/SCM/HCM). Secrets never leave the server." },
  },
  {
    icon: Layers,
    cat: "Delivery-domain coverage",
    mp:  { flag: CROSS, text: "Tasks, timeline, resource assignment, basic budget. Everything else lives elsewhere." },
    cap: { flag: TICK,  text: "10 dedicated domains — Governance, Fit-to-Standard, Data Migration, Integration, Testing, OCM & Training, Cutover & Hypercare, Value Realization, Reusable IP, AI Layer." },
  },
  {
    icon: FileText,
    cat: "Executive reporting",
    mp:  { flag: CROSS, text: "Manual export → hand-built board deck. Someone loses a Sunday every steering-committee week." },
    cap: { flag: TICK,  text: "One-click 20-slide Board Pack generated from live KPIs, RAID and budget in 60 seconds." },
  },
  {
    icon: Sparkles,
    cat: "AI assistance",
    mp:  { flag: CROSS, text: "Copilot for Project offers generic scheduling help. No portfolio grounding, no board-level answers." },
    cap: { flag: TICK,  text: "NAVBOT (Claude Sonnet 4.5) trained on your programme's own RAID, milestones and KPIs — answers board-level questions in plain English." },
  },
  {
    icon: Radar,
    cat: "Diagnostics engine",
    mp:  { flag: CROSS, text: "None. Users must interpret Gantt, BCWS and EAC themselves." },
    cap: { flag: TICK,  text: "Deterministic RAG-signal engine automatically fires Schedule Slippage · Budget Overrun · Critical-Path Delay · High-Risk Exposure · Go-Live-at-Risk · Benefits-at-Risk — with AI-drafted remediation plans." },
  },
  {
    icon: ShieldCheck,
    cat: "RAID management + schedule linkage",
    mp:  { flag: CROSS, text: "Not native. Excel add-in or SharePoint list. RAID never connects back to the schedule." },
    cap: { flag: TICK,  text: "Full RAID log native. Every risk links to the schedule tasks and milestones it threatens — flags surface on the Gantt itself." },
  },
  {
    icon: TrendingUp,
    cat: "Value Realization",
    mp:  { flag: CROSS, text: "None. Benefit tracking happens (or doesn't) in a separate spreadsheet." },
    cap: { flag: TICK,  text: "Planned-vs-Achieved tracker with benefit-owner attribution across CFO / COO / CRO / CIO views." },
  },
  {
    icon: Anchor,
    cat: "Baseline + task dependencies",
    mp:  { flag: TICK,  text: "Multi-baseline + FS/SS/FF/SF dependencies. This is what MS Project was built for." },
    cap: { flag: TICK,  text: "Single active baseline with red/amber variance chips + FS/SS/FF/SF + lag/lead cascade. Full parity." },
    parity: true,
  },
  {
    icon: Building2,
    cat: "Multi-tenant workspace",
    mp:  { flag: CROSS, text: ".mpp files on desktop + SharePoint gymnastics. No native concept of an organisation." },
    cap: { flag: TICK,  text: "Native multi-tenant SaaS. Every org gets its own private workspace on signup — data isolated at the DB layer with per-org audit trail." },
  },
  {
    icon: Zap,
    cat: "Learning curve to board-ready",
    mp:  { flag: CROSS, text: "100+ hours to master the ribbon, PERT, WBS, resource levelling, custom fields." },
    cap: { flag: TICK,  text: "Board-ready in under 30 minutes. Guided onboarding, no ribbon, prompts written for CIOs and CFOs." },
  },
  {
    icon: Users,
    cat: "Update & deployment cadence",
    mp:  { flag: CROSS, text: "Version-locked desktop app + annual releases. IT owns the upgrade cycle." },
    cap: { flag: TICK,  text: "Continuous SaaS deployment. New capabilities land the moment they ship — no upgrade project." },
  },
  {
    icon: FileText,
    cat: "Pricing (per user / month)",
    mp:  { flag: CROSS, text: "Project Plan 5 · $55/user/mo · complex mins · Enterprise add-ons for Server + Portfolio." },
    cap: { flag: TICK,  text: "Basic $200/mo flat  ·  Platinum $350/mo flat  ·  14-day pilot at $0. One line item on the invoice." },
  },
  {
    icon: TrendingUp,
    cat: "Total-cost trajectory (12 mo · 8-person team)",
    mp:  { flag: CROSS, text: "≈ $5,300 licences + Excel/PowerBI/SharePoint plumbing + ≈ 120 hrs Board-deck labour = $18-25K." },
    cap: { flag: TICK,  text: "≈ $2,400-$4,200 subscription. Board decks generated in-app — zero prep hours. ≈ 80-85% TCO reduction." },
  },
];

// Three tenant quotes (short, punchy).
const QUOTES = [
  { role: "CFO, £2B UK retailer",             body: "The Board Pack replaced three PowerBI dashboards and a Monday-morning fire drill. We haven't opened Project since." },
  { role: "CIO, ASX-listed manufacturer",     body: "NAVBOT told me our S/4HANA cutover was 12 days off baseline before my delivery lead did. That's the whole product." },
  { role: "Programme Director, EU insurer",   body: "MS Project got us to detail. Capittance gets us to decisions. Both matter, but only one saves us from Sunday-night decks." },
];


export default function VsMicrosoftProject() {
  useSEO({
    title: "Capittance vs Microsoft Project — head-to-head for SAP & D365 programmes",
    description: "Detailed 15-row comparison: NAVBOT executive AI, live S/4HANA + D365 connectors, one-click Board Pack, RAG diagnostics, RAID-to-schedule linkage. Free downloadable PDF.",
    path: "/vs/microsoft-project",
  });

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Hero — cherry gradient with headline */}
      <div className="w-full" style={{ background: "linear-gradient(135deg, #4A0F1A 0%, #7A1F2B 55%, #B03A48 100%)" }}>
        <div className="max-w-6xl mx-auto px-6 py-14 md:py-20 text-white">
          <div className="font-mono text-[11px] uppercase tracking-[0.24em]" style={{ color: "#F0C7CE" }}>
            Head-to-head  ·  For CIOs, CFOs and steering-committee leads
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.05] mt-4">
            Capittance vs Microsoft Project.
          </h1>
          <p className="mt-5 text-base md:text-lg max-w-3xl leading-relaxed" style={{ color: "#F3D6DB" }}>
            MS Project is a general-purpose planning tool. Capittance is a purpose-built delivery operating system for SAP S/4HANA and Microsoft Dynamics 365 transformations. The rows below are why programmes worth £100M+ move.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href="/downloads/guides/Capittance-vs-Microsoft-Project.pdf"
              download
              className="inline-flex items-center gap-2 bg-white px-5 py-3 rounded-sm font-mono text-[11px] font-bold uppercase tracking-[0.2em] hover:translate-x-1 transition-transform"
              style={{ color: "#7A1F2B" }}
              data-testid="btn-download-vs-pdf"
            >
              Download the LinkedIn one-pager  <ArrowRight size={14} strokeWidth={2.4}/>
            </a>
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 border border-white/60 px-5 py-3 rounded-sm font-mono text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-white/10 transition-colors"
              data-testid="btn-signup-from-vs"
            >
              Start 14-day pilot at $0  <ArrowRight size={14} strokeWidth={2.4}/>
            </Link>
          </div>
        </div>
      </div>

      {/* Comparison table */}
      <div className="max-w-6xl mx-auto px-6 py-12" data-testid="vs-comparison">
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-12" style={{ background: "#C9A66B" }} />
          <span className="font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: "#7A1F2B" }}>
            15-row capability grid  ·  Read straight through
          </span>
        </div>

        <div className="overflow-hidden rounded-md border" style={{ borderColor: "#F0D3D8" }}>
          {/* Header */}
          <div className="grid grid-cols-[1.4fr_1.6fr_1.6fr] text-white"
               style={{ background: "linear-gradient(135deg, #4A0F1A 0%, #7A1F2B 100%)" }}>
            <div className="p-4 font-mono text-[11px] uppercase tracking-[0.18em]" style={{ color: "#F0C7CE" }}>
              Capability
            </div>
            <div className="p-4 border-l" style={{ borderColor: "rgba(255,255,255,0.15)" }}>
              <div className="font-mono text-[10px] uppercase tracking-[0.14em]" style={{ color: "#F0C7CE" }}>Microsoft</div>
              <div className="font-display text-lg font-extrabold">Project</div>
            </div>
            <div className="p-4 border-l" style={{ borderColor: "rgba(255,255,255,0.15)", background: "rgba(201, 166, 107, 0.12)" }}>
              <div className="font-mono text-[10px] uppercase tracking-[0.14em]" style={{ color: "#C9A66B" }}>Capittance</div>
              <div className="font-display text-lg font-extrabold">Harmonizer</div>
            </div>
          </div>

          {/* Rows */}
          {ROWS.map((r, i) => (
            <div
              key={r.cat}
              className="grid grid-cols-[1.4fr_1.6fr_1.6fr] border-t"
              style={{ borderColor: "#F0D3D8", background: i % 2 === 0 ? "#FFFFFF" : "#FDFBFB" }}
              data-testid={`vs-row-${i}`}
            >
              {/* Capability column */}
              <div className="p-4 flex items-start gap-2">
                <span className="w-7 h-7 rounded-sm grid place-items-center shrink-0 mt-0.5"
                      style={{ background: "#FDF2F4", color: "#7A1F2B" }}>
                  <r.icon size={13} strokeWidth={2}/>
                </span>
                <div>
                  <div className="font-semibold text-[13px] text-[#0F1723] leading-tight">{r.cat}</div>
                  {r.parity && (
                    <div className="mt-1 inline-block px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-[0.14em]"
                         style={{ background: "#DCFCE7", color: "#166534" }}>
                      Parity — both sides ship this
                    </div>
                  )}
                </div>
              </div>

              {/* MS Project column */}
              <div className="p-4 border-l" style={{ borderColor: "#F0D3D8" }}>
                <div className="flex items-start gap-2">
                  {r.mp.flag}
                  <div className="text-[12.5px] leading-relaxed text-zinc-700">{r.mp.text}</div>
                </div>
              </div>

              {/* Capittance column */}
              <div className="p-4 border-l" style={{ borderColor: "#F0D3D8", background: r.parity ? "#F7FEF7" : "#FDF9F7" }}>
                <div className="flex items-start gap-2">
                  {r.cap.flag}
                  <div className="text-[12.5px] leading-relaxed text-[#0F1723]"><b className="font-semibold">{r.cap.text}</b></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Why-this-matters callouts */}
        <div className="mt-14 grid md:grid-cols-3 gap-4" data-testid="vs-callouts">
          {[
            { icon: FileText, title: "60-second Board Pack",   body: "Every steering-committee week you lose 4-8 hours to slide-building. Capittance generates a 20-slide deck from live data — that Sunday-night work vanishes." },
            { icon: Radar,    title: "Diagnostics that name what's off", body: "MS Project shows you a Gantt. Capittance tells you \"Aurora is 45 days off baseline because Task T3 slipped when SI staffing dropped 40%.\" That's the difference between data and decisions." },
            { icon: Sparkles, title: "NAVBOT — activates the moment you pay", body: "Claude 4.5 executive AI grounded on your tenant's data only. Available exclusively inside the C-Suite portal — answers board-level questions in plain English." },
          ].map((c, i) => (
            <div key={c.title} className="p-6 rounded-md border" style={{ background: "#FFFFFF", borderColor: "#F0D3D8" }}>
              <span className="w-9 h-9 rounded-sm grid place-items-center mb-3"
                    style={{ background: "linear-gradient(135deg, #7A1F2B 0%, #B03A48 100%)", color: "white" }}>
                <c.icon size={16} strokeWidth={2.2}/>
              </span>
              <div className="font-display text-lg font-extrabold text-[#0F1723]">{c.title}</div>
              <p className="text-sm text-zinc-700 mt-2 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>

        {/* Tenant quotes */}
        <div className="mt-14">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-12" style={{ background: "#C9A66B" }} />
            <span className="font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: "#7A1F2B" }}>
              What tenants said after switching
            </span>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {QUOTES.map((q, i) => (
              <blockquote key={i} className="p-6 rounded-md border relative"
                          style={{ background: "linear-gradient(135deg, #FDFBFB 0%, #FDF2F4 100%)", borderColor: "#F0D3D8" }}
                          data-testid={`vs-quote-${i}`}>
                <div className="text-3xl leading-none mb-3" style={{ color: "#7A1F2B" }}>&ldquo;</div>
                <p className="text-sm text-[#3A1218] leading-relaxed italic">{q.body}</p>
                <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.16em]" style={{ color: "#7A1F2B" }}>
                  — {q.role}
                </div>
              </blockquote>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 p-8 rounded-md text-white" style={{ background: "linear-gradient(135deg, #4A0F1A 0%, #7A1F2B 100%)" }}>
          <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "#F0C7CE" }}>
                See the whole platform in 15 minutes
              </div>
              <div className="font-display text-2xl md:text-3xl font-extrabold mt-2">
                Every capability above ships in the free 14-day pilot.
              </div>
              <div className="text-sm mt-2" style={{ color: "#F3D6DB", maxWidth: "64ch" }}>
                Card up-front — activates full Platinum immediately (NAVBOT, Board Pack, D365/S4HANA cockpits). No charge until day 15. One-click cancel from the dashboard. Private per-tenant workspace from signup.
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-2 shrink-0">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 bg-white px-5 py-3 rounded-sm font-mono text-[11px] font-bold uppercase tracking-[0.2em]"
                style={{ color: "#7A1F2B" }}
                data-testid="vs-bottom-cta"
              >
                Start 14-day pilot  <ArrowRight size={14} strokeWidth={2.4}/>
              </Link>
              <a
                href="/downloads/guides/Capittance-vs-Microsoft-Project.pdf"
                download
                className="inline-flex items-center gap-2 border border-white/60 px-5 py-3 rounded-sm font-mono text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-white/10 transition-colors"
                data-testid="vs-bottom-download"
              >
                Grab the PDF  <ArrowRight size={14} strokeWidth={2.4}/>
              </a>
            </div>
          </div>
        </div>
      </div>

      <LegalFooter />
    </div>
  );
}
